import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../../../utils/config';

interface BillReadyToPay {
  invoiceNumber: string;
  invoiceQuickbooksId: string;
  customer: string;
  invoiceTotal: number;
  invoiceBalance: number;
  billQuickbooksId: string;
  billNumber: string;
  vendor: string;
  billTotal: number;
  billBalance: number;
  billDueDate?: string;
  mappingReason?: string;
}

interface BillsSummary {
  count: number;
  totalAmount: number;
  pastDueCount: number;
  pastDueAmount: number;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface SchedulePaymentsDialogProps {
  open: boolean;
  selectedBills: BillReadyToPay[];
  onClose: () => void;
  onConfirm: (paymentMethod: string, paymentDate: string, notes: string) => Promise<void>;
}

const SchedulePaymentsDialog: React.FC<SchedulePaymentsDialogProps> = ({
  open,
  selectedBills,
  onClose,
  onConfirm,
}) => {
  const [paymentMethod, setPaymentMethod] = useState('ACH');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const totalAmount = selectedBills.reduce((sum, bill) => sum + bill.billBalance, 0);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(paymentMethod, paymentDate, notes);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Schedule Bill Payments</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          You are about to schedule {selectedBills.length} payment(s) totaling {formatCurrency(totalAmount)} to Barrett HTL.
        </Alert>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Bill #</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Due Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedBills.map((bill) => (
                <TableRow key={bill.billQuickbooksId}>
                  <TableCell>{bill.billNumber}</TableCell>
                  <TableCell>INV-{bill.invoiceNumber}</TableCell>
                  <TableCell>{bill.customer}</TableCell>
                  <TableCell align="right">{formatCurrency(bill.billBalance)}</TableCell>
                  <TableCell>
                    {bill.billDueDate ? formatDate(bill.billDueDate) : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={paymentMethod}
              label="Payment Method"
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <MenuItem value="ACH">ACH Transfer</MenuItem>
              <MenuItem value="Wire">Wire Transfer</MenuItem>
              <MenuItem value="Check">Check</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Payment Date"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Notes (optional)"
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            placeholder="e.g., Q4 2024 invoices - batch payment"
          />
        </Stack>

        <Alert severity="warning" sx={{ mt: 2 }}>
          This will add the selected bills to the reconciliation queue for approval before payment execution.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
        >
          Schedule Payments
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface BillsReadyToPaySectionProps {
  onRefresh?: () => void;
}

export const BillsReadyToPaySection: React.FC<BillsReadyToPaySectionProps> = ({ onRefresh }) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [bills, setBills] = useState<BillReadyToPay[]>([]);
  const [summary, setSummary] = useState<BillsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBills, setSelectedBills] = useState<string[]>([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);

  const fetchBills = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(`${API_URL}/api/accounting/bills/ready-to-pay`);
      if (!response) throw new Error('No response received');
      const data = await response.json();

      if (data.success) {
        setBills(data.data.bills);
        setSummary(data.data.summary);
      } else {
        setError(data.error || 'Failed to load bills');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bills');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedBills(bills.map(b => b.billQuickbooksId));
    } else {
      setSelectedBills([]);
    }
  };

  const handleSelectBill = (billId: string) => {
    setSelectedBills(prev =>
      prev.includes(billId)
        ? prev.filter(id => id !== billId)
        : [...prev, billId]
    );
  };

  const handleSchedulePayments = async (paymentMethod: string, paymentDate: string, notes: string) => {
    const response = await authenticatedFetch(
      `${API_URL}/api/accounting/bills/schedule-payments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          billIds: selectedBills,
          paymentMethod,
          paymentDate,
          notes,
        }),
      }
    );
    if (!response) throw new Error('No response received');
    const data = await response.json();

    if (data.success) {
      // Remove scheduled bills from list
      setBills(prev => prev.filter(b => !selectedBills.includes(b.billQuickbooksId)));
      setSelectedBills([]);
      setShowScheduleDialog(false);
      onRefresh?.();
    } else {
      throw new Error(data.error || 'Failed to schedule payments');
    }
  };

  const selectedTotal = bills
    .filter(b => selectedBills.includes(b.billQuickbooksId))
    .reduce((sum, b) => sum + b.billBalance, 0);

  const isPastDue = (dateString?: string) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">
            Bills Ready to Pay
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Invoices have been paid - corresponding bills are ready for payment
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchBills}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            onClick={() => setShowScheduleDialog(true)}
            disabled={selectedBills.length === 0}
          >
            Schedule Payment ({selectedBills.length})
          </Button>
        </Stack>
      </Box>

      {/* Summary */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Chip label={`${summary.count} bills ready`} color="primary" variant="outlined" />
          <Chip label={`Total: ${formatCurrency(summary.totalAmount)}`} color="primary" />
          {summary.pastDueCount > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${summary.pastDueCount} past due (${formatCurrency(summary.pastDueAmount)})`}
              color="error"
            />
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : bills.length === 0 ? (
        <Alert severity="info">
          No bills are ready to pay. Bills become ready when their corresponding invoices are paid.
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedBills.length > 0 && selectedBills.length < bills.length}
                      checked={bills.length > 0 && selectedBills.length === bills.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Bill #</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Due Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bills.map((bill) => (
                  <TableRow
                    key={bill.billQuickbooksId}
                    hover
                    onClick={() => handleSelectBill(bill.billQuickbooksId)}
                    sx={{ cursor: 'pointer' }}
                    selected={selectedBills.includes(bill.billQuickbooksId)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedBills.includes(bill.billQuickbooksId)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        INV-{bill.invoiceNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{bill.customer}</TableCell>
                    <TableCell>{bill.billNumber}</TableCell>
                    <TableCell>
                      <Chip label={bill.vendor} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(bill.billBalance)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {bill.billDueDate ? (
                        <Chip
                          label={formatDate(bill.billDueDate)}
                          size="small"
                          color={isPastDue(bill.billDueDate) ? 'error' : 'default'}
                          variant={isPastDue(bill.billDueDate) ? 'filled' : 'outlined'}
                        />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Selection Summary */}
          {selectedBills.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
              <Typography variant="body1" fontWeight="medium">
                Selected: {selectedBills.length} bill(s) totaling {formatCurrency(selectedTotal)}
              </Typography>
            </Box>
          )}
        </>
      )}

      <SchedulePaymentsDialog
        open={showScheduleDialog}
        selectedBills={bills.filter(b => selectedBills.includes(b.billQuickbooksId))}
        onClose={() => setShowScheduleDialog(false)}
        onConfirm={handleSchedulePayments}
      />
    </Box>
  );
};

export default BillsReadyToPaySection;
